import { useState, useEffect, useCallback } from 'react';
import { Search, UserMinus, UserPlus, X } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { searchUsers, type ShareUser } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useAuth } from '@/context/AuthContext';
import { teamsApi } from '@/api/teams';
import { Dialog } from '@/components/common/Dialog';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { teamMembersSchema } from './teamMembersSchema';
import type { Team, TeamMember } from '@/types/team';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

const ROLE_STYLES: Record<string, string> = {
  owner:  'bg-yellow/15 text-yellow border-yellow/30',
  manage: 'bg-purple/15 text-purple border-purple/30',
  write:  'bg-accent/15 text-accent border-accent/30',
  read:   'bg-surface-raised text-text-secondary border-border-default',
};

const ROLE_OPTIONS = ['read', 'write', 'manage'] as const;

interface Props {
  team: Team;
  isOwner: boolean;
  canManage: boolean;
  onUpdate: () => void;
}

export function TeamMembersTab({ team, isOwner, canManage, onUpdate }: Props) {
  const { t } = useI18n();
  const { params, navigate } = useRouter();
  const { user } = useAuth();
  
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const initialQuery = hashParams.get('q') || '';
  const initialPage = parseInt(hashParams.get('page') || '1', 10);

  const [page, setPage] = useState(initialPage);
  const [query, setQuery] = useState(initialQuery);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showAddMember, setShowAddMember] = useState(false);
  const [searchUserQuery, setSearchUserQuery] = useState('');
  const [isSearchingUser, setIsSearchingUser] = useState(false);
  const [searchResults, setSearchResults] = useState<ShareUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ShareUser | null>(null);
  const [addRole, setAddRole] = useState<string>('read');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      setQuery(p.get('q') || '');
      setPage(parseInt(p.get('page') || '1', 10));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const loadMembers = useCallback(() => {
    setLoading(true);
    teamsApi.listTeamMembers(team.id, query, page, 10)
      .then(res => {
        setMembers(res.members || []);
        setTotalCount(res.totalCount || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [team.id, query, page]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const updateUrl = useCallback((q: string, p: number) => {
    const base = `#/teams/${team.id}/members`;
    const searchParams = new URLSearchParams();
    if (q) searchParams.set('q', q);
    if (p > 1) searchParams.set('page', String(p));
    const qs = searchParams.toString();
    window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
    setQuery(q);
    setPage(p);
  }, [team.id]);

  const handleSearch = useCallback((newQuery: string) => {
    updateUrl(newQuery, 1);
  }, [updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    updateUrl(query, newPage);
  }, [updateUrl, query]);

  const resetAddMember = () => {
    setSearchUserQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setAddRole('read');
  };

  useEffect(() => {
    if (!showAddMember || searchUserQuery.length < 2) { setSearchResults([]); return; }
    setIsSearchingUser(true);
    const timer = setTimeout(() => {
      searchUsers(searchUserQuery)
        .then(res => {
          // We can't strictly filter existing members here since we don't have all members locally,
          // but we can filter the owner and ourselves
          setSearchResults((res.users || []).filter(u => u.id !== user?.id && u.id !== team.ownerId));
        })
        .catch(() => setSearchResults([]))
        .finally(() => setIsSearchingUser(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchUserQuery, showAddMember, team.ownerId, user?.id]);

  const handleAddMember = async () => {
    if (!selectedUser) return;
    setAdding(true);
    try {
      await teamsApi.addTeamMember(team.id, selectedUser.id, addRole);
      setShowAddMember(false);
      resetAddMember();
      loadMembers();
      onUpdate();
    } catch (e) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await teamsApi.removeTeamMember(team.id, userId);
      loadMembers();
      onUpdate();
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateRole = async (userId: string, role: string) => {
    try {
      await teamsApi.updateTeamMemberRole(team.id, userId, role);
      loadMembers();
      onUpdate();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title">{t('team.members')}</h3>
        {canManage && (
          <button
            onClick={() => setShowAddMember(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <UserPlus size={14} />
            {t('team.addMember')}
          </button>
        )}
      </div>

      <div className="mb-4">
        <FilterBar
          schema={teamMembersSchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      <Dialog open={showAddMember} onClose={() => { setShowAddMember(false); resetAddMember(); }} title={t('team.addMember')}>
        <div className="space-y-4">
          {/* Selected user chips */}
          {selectedUser && (
            <div>
              <div className="text-xs font-medium text-text-secondary mb-2">{t('sharing.selectedUsers')}</div>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-subtle text-accent border border-accent/20">
                {selectedUser.name}
                <button onClick={() => setSelectedUser(null)} className="hover:text-red transition-colors cursor-pointer"><X size={12} /></button>
              </span>
            </div>
          )}

          {/* User search */}
          {!selectedUser && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
                <input
                  type="text"
                  value={searchUserQuery}
                  onChange={e => setSearchUserQuery(e.target.value)}
                  placeholder={t('sharing.searchPlaceholder')}
                  className="input-field w-full py-2 pl-10 pr-3"
                  autoFocus
                />
              </div>

              <div className="border border-border-default rounded-md max-h-48 overflow-y-auto">
                {isSearchingUser ? (
                  <div className="p-6 text-sm text-text-muted text-center">{t('sharing.searching')}</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((u, i) => (
                    <button key={u.id} onClick={() => setSelectedUser(u)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 transition-colors cursor-pointer ${i < searchResults.length - 1 ? 'border-b border-border-subtle' : ''} hover:bg-bg-hover`}>
                      <div className="text-left">
                        <div className="text-sm font-medium text-text-primary">{u.name}</div>
                      </div>
                    </button>
                  ))
                ) : searchUserQuery.length >= 2 ? (
                  <div className="p-6 text-sm text-text-muted text-center">{t('sharing.noUsersFound')}</div>
                ) : (
                  <div className="p-6 text-sm text-text-muted text-center">{t('sharing.searchHint')}</div>
                )}
              </div>
            </>
          )}

          {/* Role selector */}
          {selectedUser && (
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('team.members')}</label>
              <select
                value={addRole}
                onChange={e => setAddRole(e.target.value)}
                className="input-field w-full py-2 px-3 cursor-pointer"
              >
                {ROLE_OPTIONS.map(r => (
                  <option key={r} value={r}>{t(`team.role.${r}` as any)}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setShowAddMember(false); resetAddMember(); }}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleAddMember}
              disabled={!selectedUser || adding}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
            >
              <UserPlus size={14} />
              {adding ? t('common.loading') : t('team.addMember')}
            </button>
          </div>
        </div>
      </Dialog>

      <div className="card overflow-hidden">
        {/* Desktop Table View */}
        <table className="w-full border-collapse hidden sm:table">
          <thead>
            <tr className="divider">
              <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                {t('team.members')}
              </th>
              <th className="w-[140px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                {t('team.members.field.role')}
              </th>
              <th className="w-[50px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
            </tr>
          </thead>
          <tbody>
            {page === 1 && !query && (
              <tr className={`hover:bg-bg-hover transition-colors ${members.length > 0 ? 'divider' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-yellow/15 flex items-center justify-center text-yellow text-sm font-semibold shrink-0">
                      👑
                    </div>
                    <span className="text-sm font-medium text-text-primary truncate">{team.ownerId === user?.id ? user?.name || 'You' : team.ownerId}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${ROLE_STYLES.owner}`}>
                    {t('team.role.owner')}
                  </span>
                </td>
                <td className="px-4 py-3" />
              </tr>
            )}

            {!loading && members.map((member, index) => (
              <tr
                key={member.userId}
                className={`hover:bg-bg-hover transition-colors ${index < members.length - 1 ? 'divider' : ''}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-accent/15 flex items-center justify-center text-accent text-sm font-semibold shrink-0">
                      {(member.userName || '?').charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-text-primary truncate">{member.userName || member.userId}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {canManage ? (
                    <select
                      value={member.role}
                      onChange={e => handleUpdateRole(member.userId, e.target.value)}
                      className="input-field px-2 py-1 text-xs cursor-pointer"
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r} value={r}>{t(`team.role.${r}` as any)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${ROLE_STYLES[member.role] || ROLE_STYLES.read}`}>
                      {t(`team.role.${member.role}` as any)}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <button
                      onClick={() => handleRemoveMember(member.userId)}
                      className="p-1.5 text-text-muted hover:text-red transition-colors cursor-pointer"
                      title={t('team.removeMember')}
                    >
                      <UserMinus size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile View */}
        <div className="sm:hidden">
          {page === 1 && !query && (
            <div className={`p-4 hover:bg-bg-hover transition-colors ${members.length > 0 ? 'divider' : ''}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-yellow/15 flex items-center justify-center text-yellow text-sm font-semibold shrink-0">
                    👑
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{team.ownerId === user?.id ? user?.name || t('team.you') : team.ownerId}</p>
                    <p className="text-xs text-text-muted">{t('team.role.owner')}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${ROLE_STYLES.owner}`}>
                  {t('team.role.owner')}
                </span>
              </div>
            </div>
          )}

          {!loading && members.map((member, index) => (
            <div
              key={member.userId}
              className={`p-4 hover:bg-bg-hover transition-colors ${index < members.length - 1 ? 'divider' : ''}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-accent/15 flex items-center justify-center text-accent text-sm font-semibold shrink-0">
                    {(member.userName || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{member.userName || member.userId}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {canManage ? (
                    <select
                      value={member.role}
                      onChange={e => handleUpdateRole(member.userId, e.target.value)}
                      className="input-field px-2 py-1 text-xs cursor-pointer"
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r} value={r}>{t(`team.role.${r}` as any)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${ROLE_STYLES[member.role] || ROLE_STYLES.read}`}>
                      {t(`team.role.${member.role}` as any)}
                    </span>
                  )}
                  {canManage && (
                    <button
                      onClick={() => handleRemoveMember(member.userId)}
                      className="p-1.5 text-text-muted hover:text-red transition-colors cursor-pointer"
                      title={t('team.removeMember')}
                    >
                      <UserMinus size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="py-8"><LoadingSpinner /></div>
        )}

        {!loading && members.length === 0 && (
          <div className="py-8 text-center text-text-muted text-sm">
            {t('team.empty')}
          </div>
        )}

        {!loading && totalCount > 10 && (
          <div className="p-4 border-t border-border-subtle flex justify-center">
            <Pagination
              page={page}
              totalPages={Math.ceil(totalCount / 10)}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
